import { Deg2Rad, Rad2Deg } from "../math/MathEx.js";
export var MoonPhase;
(function (MoonPhase) {
    MoonPhase["Full"] = "Full";
    MoonPhase["WaningGibbous"] = "WaningGibbous";
    MoonPhase["LastQuarter"] = "LastQuarter";
    MoonPhase["WaningCrescent"] = "WaningCrescent";
    MoonPhase["New"] = "New";
    MoonPhase["WaxingCrescent"] = "WaxingCrescent";
    MoonPhase["FirstQuarter"] = "FirstQuarter";
    MoonPhase["WaxingGibbous"] = "WaxingGibbous";
    MoonPhase["None"] = "None";
})(MoonPhase || (MoonPhase = {}));
export class MoonObjectCalculator {
    static FixedAngle(a) {
        return a - 360.0 * Math.floor(a / 360.0);
    }
    static Age(jd) {
        const synodicMonth = 29.53058868;
        const moonAge = MoonObjectCalculator.AgeDegrees(jd);
        return ((synodicMonth * MoonObjectCalculator.FixedAngle(moonAge)) / 360.0);
    }
    static Phase(jd) {
        const PRECISION = 0.05;
        const NEW = 0 / 4.0;
        const FIRST = 1 / 4.0;
        const FULL = 2 / 4.0;
        const LAST = 3 / 4.0;
        const NEXTNEW = 4 / 4.0;
        let nextJd = jd + 1;
        const age = MoonObjectCalculator.AgeDegrees(jd);
        const moonPhase = MoonObjectCalculator.FixedAngle(age) / 360.0;
        const moonPhaseNext = MoonObjectCalculator.FixedAngle(MoonObjectCalculator.AgeDegrees(nextJd)) / 360.0;
        if (moonPhase > NEW && moonPhase < FIRST - PRECISION)
            return MoonPhase.WaxingCrescent;
        if (moonPhase >= FIRST - PRECISION && moonPhase <= FIRST) {
            if (moonPhaseNext >= FIRST)
                return MoonPhase.FirstQuarter;
            else
                return MoonPhase.WaxingCrescent;
        }
        if (moonPhase < FULL - PRECISION)
            return MoonPhase.WaxingGibbous;
        if (moonPhase >= FULL - PRECISION && moonPhase <= FULL) {
            if (moonPhaseNext >= FULL)
                return MoonPhase.Full;
            else
                return MoonPhase.WaxingGibbous;
        }
        if (moonPhase < LAST - PRECISION)
            return MoonPhase.WaningGibbous;
        if (moonPhase >= LAST - PRECISION && moonPhase <= LAST) {
            if (moonPhaseNext >= LAST)
                return MoonPhase.LastQuarter;
            else
                return MoonPhase.WaningGibbous;
        }
        if (moonPhase < NEXTNEW - PRECISION)
            return MoonPhase.WaningCrescent;
        if (moonPhase >= NEXTNEW - PRECISION) {
            if (moonPhase - moonPhaseNext > 0)
                return MoonPhase.New;
            else
                return MoonPhase.WaningCrescent;
        }
        return MoonPhase.None;
    }
    /// https://bazaar.launchpad.net/~keturn/py-moon-phase/trunk/view/head:/moon.py
    /// Ported from \"A Moon for the Sun\" (aka moontool.c), a program by the
    /// venerable John Walker.  He used algoritms from \"Practical Astronomy
    /// With Your Calculator\" by Peter Duffett-Smith, Second Edition.
    /// For the full history of the code, as well as references to other
    /// reading material and other entertainments, please refer to John
    /// Walker's website,
    /// http://www.fourmilab.ch/
    /// (Look under the Science/Astronomy and Space heading.)
    static AgeDegrees(jd) {
        const epoch = 2444238.5;
        const eclipticLongitudeEpoch = 278.83354;
        const eclipticLongitudePerigee = 282.596403;
        const eccentricity = 0.016718;
        const moonMeanPerigeeEpoch = 349.383063;
        const moonMeanLongitudeEpoch = 64.975464;
        const day = jd - epoch;
        const N = MoonObjectCalculator.FixedAngle((360.0 / 365.2422) * day);
        const M = MoonObjectCalculator.FixedAngle(N + eclipticLongitudeEpoch - eclipticLongitudePerigee);
        let Ec = MoonObjectCalculator.Kepler(M * Deg2Rad, eccentricity);
        Ec =
            Math.sqrt((1 + eccentricity) / (1 - eccentricity)) *
                Math.tan(Ec / 2.0);
        Ec = 2 * Math.atan(Ec) * Rad2Deg;
        const lambdaSun = MoonObjectCalculator.FixedAngle(Ec + eclipticLongitudePerigee);
        const moonLongitude = MoonObjectCalculator.FixedAngle(13.1763966 * day + moonMeanLongitudeEpoch);
        const MM = MoonObjectCalculator.FixedAngle(moonLongitude - 0.1114041 * day - moonMeanPerigeeEpoch);
        const evection = 1.2739 * Math.sin((2 * (moonLongitude - lambdaSun) - MM) * Deg2Rad);
        const annualEq = 0.1858 * Math.sin(M * Deg2Rad);
        const A3 = 0.37 * Math.sin(M * Deg2Rad);
        const MmP = MM + evection - annualEq - A3;
        const mEc = 6.2886 * Math.sin(MmP * Deg2Rad);
        const A4 = 0.214 * Math.sin(2 * MmP * Deg2Rad);
        const lP = moonLongitude + evection + mEc - annualEq + A4;
        const constiation = 0.6583 * Math.sin(2 * (lP - lambdaSun) * Deg2Rad);
        const lPP = lP + constiation;
        return lPP - lambdaSun;
    }
    static Kepler(meanAnomaly, eccentricity) {
        const epsilon = 1e-6;
        let e = meanAnomaly;
        while (true) {
            const delta = e - eccentricity * Math.sin(e) - meanAnomaly;
            e -= delta / (1.0 - eccentricity * Math.cos(e));
            if (Math.abs(delta) <= epsilon)
                break;
        }
        return e;
    }
}
